// ---------------------------------------------------------------------------
// Toast Store — lightweight notification system for editor operations.
//
// Usage: useToastStore.getState().addToast({ message: 'Copied!' })
// ---------------------------------------------------------------------------

import { create } from 'zustand'

export interface Toast {
  id: string
  message: string
  type: 'info' | 'success' | 'error' | 'warning'
  duration: number
}

interface ToastState {
  toasts: Toast[]
  addToast: (toast: {
    message: string
    type?: Toast['type']
    duration?: number
    /**
     * Opt-in idempotence (default `false`). When `true`, an identical
     * `{message, type}` that is still live is suppressed — for the ONE case a
     * single error is fanned out to two code paths (F32: the hero create-
     * failure that both the producer and the caller surfaced). Left `false`,
     * the toast STACKS like every other toast, so two genuinely-distinct
     * failures with the same text (e.g. saving block A then block B, both
     * 'Save failed: Failed to fetch') each show — the user never mistakes a
     * silent drop for success (persona-ux-08 / CA-3). Most callers want the
     * default; only deliberately-idempotent surfaces pass `dedupe: true`.
     */
    dedupe?: boolean
  }) => void
  removeToast: (id: string) => void
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  addToast: ({ message, type = 'info', duration = 2500, dedupe = false }) => {
    // Opt-in dedup ONLY. A store-wide dedup (the original F32 attempt)
    // silently swallowed genuinely-repeated distinct failures — two failed
    // saves with identical text inside the live window dropped the second,
    // so the user thought it succeeded. Scoped to `dedupe: true`, the swallow
    // is confined to callers that explicitly want one-shot semantics.
    if (dedupe) {
      const alreadyLive = useToastStore
        .getState()
        .toasts.some((t) => t.message === message && t.type === type)
      if (alreadyLive) return
    }

    const id = crypto.randomUUID().slice(0, 8)
    const toast: Toast = { id, message, type, duration }

    set((state) => ({ toasts: [...state.toasts, toast] }))

    // Auto-remove after duration
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }))
    }, duration)
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
}))

/**
 * Shorthand for adding a toast from anywhere (no hook needed).
 *
 * Each accepts an optional `{ dedupe }` so a deliberately-idempotent surface
 * (e.g. one logical failure F32 wants shown once) can opt into suppression
 * without swallowing distinct same-text failures elsewhere. Default is the
 * normal stack-everything behavior.
 */
interface ToastOpts {
  dedupe?: boolean
}
export const toast = {
  info: (message: string, opts?: ToastOpts) =>
    useToastStore.getState().addToast({ message, type: 'info', dedupe: opts?.dedupe }),
  success: (message: string, opts?: ToastOpts) =>
    useToastStore.getState().addToast({ message, type: 'success', dedupe: opts?.dedupe }),
  error: (message: string, opts?: ToastOpts) =>
    useToastStore.getState().addToast({ message, type: 'error', dedupe: opts?.dedupe }),
  warning: (message: string, opts?: ToastOpts) =>
    useToastStore.getState().addToast({ message, type: 'warning', dedupe: opts?.dedupe }),
}
